import { useEffect, useState } from "react";
import api, { fileUrl, uploadFile, asItems, asTotal, API } from "@/lib/api";
import { money, dt, SHOPIFY_SYNC_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState, Pagination } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, Search, History, SlidersHorizontal, ImageIcon, Download, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyProduct = {
  nombre: "", descripcion: "", categoria: "", marca: "", fotos: [], precio: 0, costo: 0,
  sku: "", stock_actual: 0, stock_minimo: 5, es_combo: false, items_combo: [], tags: [], activo: true,
};

function StockBadge({ p }) {
  if (p.stock_actual === 0) return <Badge className="bg-red-50 text-red-600 rounded-full border-0">Sin stock</Badge>;
  if (p.stock_actual <= (p.stock_minimo || 5)) return <Badge className="bg-amber-50 text-amber-600 rounded-full border-0">Stock bajo · {p.stock_actual}</Badge>;
  return <Badge className="bg-emerald-50 text-emerald-600 rounded-full border-0">{p.stock_actual} u.</Badge>;
}

export default function Products() {
  const { user } = useAuth();
  const canEdit = ["admin", "contadora"].includes(user?.role);
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("todas");
  const [fMarca, setFMarca] = useState("todas");
  const [fStock, setFStock] = useState(false);
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 50;
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjust, setAdjust] = useState({ cantidad: 0, motivo: "" });
  const [histFor, setHistFor] = useState(null);
  const [movs, setMovs] = useState([]);

  const filterParams = () => {
    const params = {};
    if (q) params.q = q;
    if (fCat !== "todas") params.categoria = fCat;
    if (fMarca !== "todas") params.marca = fMarca;
    if (fStock) params.stock_bajo = "true";
    return params;
  };

  const load = async () => {
    const params = { ...filterParams(), skip, limit: LIMIT };
    const r = await api.get("/products", { params });
    setProducts(asItems(r));
    setTotal(asTotal(r));
  };

  useEffect(() => { api.get("/categorias").then((r) => setCats(r.data.filter(Boolean))); }, []);
  useEffect(() => { api.get("/products/marcas").then((r) => setMarcas(r.data)); }, []);
  useEffect(() => { api.post("/notifications/marcar-leidas", null, { params: { tipo: "stock_bajo" } }); }, []);
  useEffect(() => { setSkip(0); /* eslint-disable-next-line */ }, [q, fCat, fMarca, fStock]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, fCat, fMarca, fStock, skip]);

  const exportCsv = () => {
    const token = localStorage.getItem("ms_token");
    const params = new URLSearchParams(filterParams());
    window.open(`${API}/products/export?${params.toString()}&auth=${token}`, "_blank");
  };

  const openNew = () => { setForm(emptyProduct); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...emptyProduct, ...p }); setEditing(p.id); };

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const payload = { ...form, precio: Number(form.precio), costo: Number(form.costo),
        stock_actual: Number(form.stock_actual), stock_minimo: Number(form.stock_minimo),
        tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Producto guardado");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const { path } = await uploadFile(file); setForm((f) => ({ ...f, fotos: [path] })); toast.success("Foto subida"); }
    catch { toast.error("No se pudo subir la foto"); }
  };

  const doAdjust = async () => {
    if (!adjust.motivo.trim()) return toast.error("El motivo es obligatorio");
    if (!Number(adjust.cantidad)) return toast.error("Indicá una cantidad distinta de cero");
    try {
      await api.post(`/products/${adjustFor.id}/ajuste-stock`, { cantidad: Number(adjust.cantidad), motivo: adjust.motivo });
      toast.success("Stock ajustado"); setAdjustFor(null); setAdjust({ cantidad: 0, motivo: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openHist = async (p) => {
    setHistFor(p);
    const r = await api.get(`/products/${p.id}/movimientos`, { params: { limit: 200 } });
    setMovs(asItems(r));
  };

  const exportMovsCsv = () => {
    if (!histFor) return;
    const token = localStorage.getItem("ms_token");
    window.open(`${API}/products/${histFor.id}/movimientos/export?auth=${token}`, "_blank");
  };

  const cambiarImagen = async (p, file) => {
    if (!file) return;
    try {
      const { path } = await uploadFile(file);
      await api.put(`/products/${p.id}`, { ...p, fotos: [path] });
      toast.success("Imagen actualizada"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo cambiar la imagen"); }
  };
  const eliminarImagen = async (p) => {
    if (!window.confirm(`¿Eliminar la imagen de ${p.nombre}?`)) return;
    try { await api.delete(`/products/${p.id}/imagen`); toast.success("Imagen eliminada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      <PageHeader title="Productos e inventario" subtitle="Catálogo, combos y control de stock.">
        <Button variant="outline" onClick={exportCsv} className="rounded-full gap-2" data-testid="export-products-csv"><Download className="h-4 w-4" /> Exportar CSV</Button>
        {canEdit && (
          <Button onClick={openNew} data-testid="new-product-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
            <Plus className="h-4 w-4" /> Nuevo producto
          </Button>
        )}
      </PageHeader>

      <div className="ms-card p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="product-search" placeholder="Buscar por nombre, SKU o marca…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-xl border-0 bg-muted" />
        </div>
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger className="w-44 rounded-xl" data-testid="filter-categoria"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las categorías</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fMarca} onValueChange={setFMarca}>
          <SelectTrigger className="w-40 rounded-xl" data-testid="filter-marca"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las marcas</SelectItem>
            {marcas.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm px-2">
          <Switch checked={fStock} onCheckedChange={setFStock} data-testid="filter-stock-bajo" /> Solo stock bajo
        </label>
      </div>

      {!products ? <Loader /> : products.length === 0 ? (
        <EmptyState icon={Package} title="Sin productos" subtitle="No encontramos productos con esos filtros." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-card-${p.id}`} className="ms-card ms-lift overflow-hidden flex flex-col">
              <div className="h-36 bg-muted relative">
                {p.fotos?.[0] ? (
                  <img src={p.fotos[0].startsWith("http") ? p.fotos[0] : fileUrl(p.fotos[0])} alt={p.nombre} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="h-8 w-8" /></div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  {p.es_combo && <Badge className="bg-secondary text-secondary-foreground border-0 rounded-full">Combo</Badge>}
                  {!p.activo && <Badge className="bg-gray-200 text-gray-600 border-0 rounded-full">Inactivo</Badge>}
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <p className="text-xs text-muted-foreground">{p.categoria} · {p.marca}</p>
                <h3 className="font-semibold leading-tight mt-0.5">{p.nombre}</h3>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-head font-semibold text-primary">{money(p.precio)}</span>
                  <StockBadge p={p} />
                </div>
                <div className="flex gap-2 mt-4">
                  {canEdit && <Button size="sm" variant="outline" className="rounded-full flex-1" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`}>Editar</Button>}
                  {canEdit && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAdjustFor(p)} data-testid={`adjust-${p.id}`}><SlidersHorizontal className="h-4 w-4" /></Button>}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => openHist(p)} data-testid={`hist-${p.id}`}><History className="h-4 w-4" /></Button>
                </div>
                {canEdit && (
                  <div className="flex gap-2 mt-2">
                    <label className="flex-1">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => cambiarImagen(p, e.target.files?.[0])} data-testid={`cambiar-imagen-input-${p.id}`} />
                      <span className="w-full text-xs rounded-full border border-border px-2 py-1.5 flex items-center justify-center gap-1 cursor-pointer hover:bg-accent" data-testid={`cambiar-imagen-${p.id}`}>
                        <RefreshCw className="h-3 w-3" /> Cambiar imagen
                      </span>
                    </label>
                    {p.fotos?.[0] && (
                      <button onClick={() => eliminarImagen(p)} className="text-xs rounded-full border border-border px-2 py-1.5 flex items-center gap-1 text-red-500 hover:bg-red-50" data-testid={`eliminar-imagen-${p.id}`}>
                        <Trash2 className="h-3 w-3" /> Eliminar
                      </button>
                    )}
                  </div>
                )}
                {canEdit && (
                  <div className="mt-3 pt-3 border-t border-border/60">
                    <span className="text-[11px] text-muted-foreground" data-testid={`shopify-estado-${p.id}`}>
                      {p.shopify_variant_id ? `Web (Shopify): ${SHOPIFY_SYNC_LABELS[p.shopify_sync_estado] || "Vinculado"}` : "Web (Shopify): sin vincular"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Pagination skip={skip} limit={LIMIT} total={total} onChange={setSkip} />

      {/* Edit / new dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nuevo producto" : "Editar producto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editing !== "new" && form.shopify_variant_id && (
              <p className="text-xs rounded-xl bg-muted px-3 py-2" data-testid="pf-aviso-web">
                Este producto está vinculado con la web (Shopify). El nombre, la descripción, el precio, las fotos y si está activo los define la web: lo que cambies acá se reemplaza cuando se aplique una actualización de la web.
              </p>
            )}
            <div><Label>Nombre</Label><Input data-testid="pf-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="rounded-xl mt-1" /></div>
            <div><Label>Descripción</Label><Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className="rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoría</Label><Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Marca</Label><Input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Precio (RD$)</Label><Input data-testid="pf-precio" type="number" value={form.precio} onChange={(e) => setForm({ ...form, precio: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Costo (RD$)</Label><Input type="number" value={form.costo} onChange={(e) => setForm({ ...form, costo: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Stock mínimo</Label><Input type="number" value={form.stock_minimo} onChange={(e) => setForm({ ...form, stock_minimo: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            {editing === "new" && (
              <div><Label>Stock inicial</Label><Input type="number" value={form.stock_actual} onChange={(e) => setForm({ ...form, stock_actual: e.target.value })} className="rounded-xl mt-1" /></div>
            )}
            <div><Label>Tags (separados por coma)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="rounded-xl mt-1" /></div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-sm">Es combo</span>
              <Switch checked={form.es_combo} onCheckedChange={(v) => setForm({ ...form, es_combo: v })} />
            </div>
            <div>
              <Label>Foto</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.fotos?.[0] && <img src={form.fotos[0].startsWith("http") ? form.fotos[0] : fileUrl(form.fotos[0])} alt="" className="h-14 w-14 rounded-xl object-cover" />}
                <Input type="file" accept="image/*" onChange={onPhoto} className="rounded-xl" data-testid="pf-foto" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-sm">Producto activo</span>
              <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="pf-save" className="rounded-full bg-primary hover:bg-primary/90">{saving ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust stock */}
      <Dialog open={!!adjustFor} onOpenChange={(o) => !o && setAdjustFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ajustar stock</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{adjustFor?.nombre} · actual: {adjustFor?.stock_actual} u.</p>
          <div><Label>Cantidad (+ entra, - sale)</Label><Input data-testid="adjust-cantidad" type="number" value={adjust.cantidad} onChange={(e) => setAdjust({ ...adjust, cantidad: e.target.value })} className="rounded-xl mt-1" /></div>
          <div><Label>Motivo (obligatorio)</Label><Textarea data-testid="adjust-motivo" value={adjust.motivo} onChange={(e) => setAdjust({ ...adjust, motivo: e.target.value })} className="rounded-xl mt-1" placeholder="Ej: conteo físico, producto dañado…" /></div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAdjustFor(null)}>Cancelar</Button>
            <Button onClick={doAdjust} data-testid="adjust-save" className="rounded-full bg-primary hover:bg-primary/90">Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={!!histFor} onOpenChange={(o) => !o && setHistFor(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex-row items-center justify-between">
            <DialogTitle>Movimientos · {histFor?.nombre}</DialogTitle>
          </DialogHeader>
          <Button variant="outline" size="sm" className="rounded-full gap-2 w-fit" onClick={exportMovsCsv} data-testid="export-movimientos-csv"><Download className="h-4 w-4" /> Exportar CSV</Button>
          {movs.length === 0 ? <p className="text-sm text-muted-foreground py-4">Sin movimientos.</p> : (
            <div className="space-y-2">
              {movs.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
                  <div>
                    <p className="text-sm font-medium capitalize">{m.tipo.replace("_", " ")}</p>
                    <p className="text-xs text-muted-foreground">{m.motivo} · {m.usuario} · {dt(m.fecha)}</p>
                  </div>
                  <span className={`font-semibold ${m.cantidad >= 0 ? "text-emerald-600" : "text-red-500"}`}>{m.cantidad >= 0 ? "+" : ""}{m.cantidad}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
